import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { REQUEST_METHODS } from '@app/constants/request-methods';
import { getTimeZoneOffset } from '@app/helpers/date/date-helper';
import { RequestBuilderService } from '@app/services/request-builder/request-builder.service';
import { IData, IPaginatedData, ITableAction } from '@app/types/data-tables';
import { IClientDetail, IVendorSupplierDetails } from '../../types/client-detail';
import { IClientUserRejectFields } from '../../types/client-user';
import { IQrCodeDetail } from '../../types/qr-code-detail';

@Injectable({
    providedIn: 'root',
})
export class ClientService {
    private requestBuilderService = inject(RequestBuilderService);

    getClientById(clientId: string) {
        const apiUrl = REQUEST_METHODS.V1.Companies.ClientDetailById(
            clientId,
            getTimeZoneOffset(),
        );
        return this.requestBuilderService.get<IClientDetail>(apiUrl);
    }

    getVendorById(clientId: string) {
        const apiUrl = REQUEST_METHODS.V1.Companies.VendorDetailById(clientId);
        return this.requestBuilderService.get<IClientDetail>(apiUrl);
    }

    getVendorSupplierById(
        id: string,
        { page, pageSize, search, sort, sortBy }: Omit<ITableAction, 'onFetch'>,
    ) {
        const queryData = {
            PageNumber: page,
            PageSize: pageSize,
            SearchQuery: search ?? '',
            sort: sort ?? '',
            sortBy: sortBy ?? '',
        };
        const paramsData = {};

        Object.entries(queryData).forEach(([key, value]) => {
            if (value) {
                Object.assign(paramsData, { [key]: value });
            }
        });

        const params = new HttpParams({ fromObject: paramsData });
        const apiUrl = REQUEST_METHODS.V1.Companies.VendorsById(id);
        return this.requestBuilderService.get<
            IPaginatedData<IData<IVendorSupplierDetails>>
        >(`${apiUrl}?${params.toString()}`);
    }
    getQrOnBoardingCodes(
        id: string,
        offset: number,
        { page, pageSize, search, sort, sortBy }: Omit<ITableAction, 'onFetch'>,
    ) {
        const queryData = {
            PageNumber: page,
            PageSize: pageSize,
            SearchQuery: search ?? '',
            sort: sort ?? '',
            sortBy: sortBy ?? '',
        };
        const paramsData = {};

        Object.entries(queryData).forEach(([key, value]) => {
            if (value) {
                Object.assign(paramsData, { [key]: value });
            }
        });

        const params = new HttpParams({ fromObject: paramsData });
        const apiUrl = REQUEST_METHODS.V1.Companies.QrOnboardingCodes(id, offset);
        return this.requestBuilderService.get<IPaginatedData<IData<IQrCodeDetail>>>(
            `${apiUrl}?${params.toString()}`,
        );
    }

    approveClient({ mdmId, id }: { mdmId: string; id: string }) {
        const apiUrl = REQUEST_METHODS.V1.Companies.ApprovedClient(mdmId, id);
        return this.requestBuilderService.put<number>(apiUrl, {});
    }

    rejectClient({
        mdmId,
        id,
        payload,
    }: {
        mdmId: string;
        id: string;
        payload: IClientUserRejectFields;
    }) {
        const apiUrl = REQUEST_METHODS.V1.Companies.RejectClient(mdmId, id);
        return this.requestBuilderService.put<number>(apiUrl, { ...payload });
    }
}
